import numericStringToFloat from 'services/numbers/numericStringToFloat';

import getSubproductDetailsTotalExpense from './getSubproductDetailsTotalExpense';
import getSubproductSellingPrice from './getSubproductSellingPrice';
import getSubproductTotalExpense from './getSubproductTotalExpense';
import IDetail from './IDetail';
import ISubproduct from './ISubproduct';

// A material row, possibly priced from its details (sub rows) with waste of their own
type IWasteSubproduct = ISubproduct & {
  calculatePriceFromDetails?: boolean;
  details?: IDetail[];
};

// The part of the expense of the details that is their waste, as a factor of the expense
// without it (e.g. 10 % waste: 100 / 110); 1 when the price is not from the details
const getDetailsWithoutWasteFactor = (subproduct: IWasteSubproduct): number => {
  if (!subproduct.calculatePriceFromDetails || !subproduct.details?.length) {
    return 1;
  }

  const withWaste = getSubproductDetailsTotalExpense(subproduct.details);
  const withoutWaste = getSubproductDetailsTotalExpense(
    subproduct.details.map((detail) => ({ ...detail, wastePercentage: 0 }))
  );

  return withWaste > 0 ? withoutWaste / withWaste : 1;
};

/**
 * The part of the material rows that comes from their waste, for the whole position: the
 * expense of the material bought for the waste (e.g. 10 % waste of 100 € makes 10 € of
 * 110 €) and the same share of the selling price of the row, their margin and margin %.
 * The waste is that of the row and, for a row priced from its sub rows, of the sub rows.
 */
const getWasteEffect = (
  subproducts: IWasteSubproduct[],
  quantity: number
): {
  contributionMargin: number;
  contributionMarginRatio: number;
  expense: number;
  sellingPrice: number;
} => {
  const perUnit = subproducts.reduce(
    (sum, subproduct) => {
      const expense = getSubproductTotalExpense(subproduct);
      if (!(expense > 0)) {
        return sum;
      }

      const rowWasteFactor =
        1 +
        numericStringToFloat(String(subproduct.wastePercentage ?? '')) / 100;
      const withoutWaste =
        (expense / rowWasteFactor) * getDetailsWithoutWasteFactor(subproduct);
      // The share of the waste in the expense of the row, and so in its selling price
      const share = (expense - withoutWaste) / expense;
      if (!(share > 0)) {
        return sum;
      }

      return {
        expense: sum.expense + expense * share,
        sellingPrice:
          sum.sellingPrice + getSubproductSellingPrice(subproduct) * share,
      };
    },
    { expense: 0, sellingPrice: 0 }
  );

  const sellingPrice = perUnit.sellingPrice * quantity;
  const expense = perUnit.expense * quantity;
  const contributionMargin = sellingPrice - expense;

  return {
    contributionMargin,
    contributionMarginRatio:
      sellingPrice === 0 ? 0 : (contributionMargin / sellingPrice) * 100,
    expense,
    sellingPrice,
  };
};

export default getWasteEffect;
